import { inject, Injectable, NgZone, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { Deconnexion } from './session';
import { URL_LISTE_DOSSIERS } from './chemins';
import { estErreurChargementEcran, rechargerApresEchecChargement } from './chargement-ecran';

/** Échec d'une navigation affiché en plein écran (jamais de page blanche). */
interface EchecNavigation {
  /** Adresse demandée (Réessayer). */
  url: string;
  titre: string;
  texte: string;
  /** Écran (fichier JavaScript) non chargé : Réessayer recharge la page vers l'adresse demandée. */
  rechargement: boolean;
}

/** Délai avant la barre de progression d'un changement d'écran dans le même cadre (évite un clignotement). */
const DELAI_BARRE = 300;

/**
 * Zone d'une adresse : « /dossier/118 » pour un dossier, « signin » pour la connexion, sinon « app » (pages hors
 * dossier). Changer de zone (ouvrir un dossier, en changer) affiche l'écran de chargement plein écran.
 */
export function zoneNavigation(url: string): string {
  const chemin = url.split(/[?#]/)[0];
  const dossier = /^\/dossier\/[^/]+/.exec(chemin);
  if (dossier) {
    return dossier[0];
  }
  return chemin === '/signin' || chemin.startsWith('/signin/') ? 'signin' : 'app';
}

/**
 * État des navigations, affiché par app-navigation-etat (layout/navigation-etat) :
 * - ouverture ou changement de dossier, démarrage (F5, lien direct) : écran de chargement dès le début (garde, résolveur) ;
 * - changement d'écran dans le même cadre : barre de progression après DELAI_BARRE ;
 * - échec (résolveur, garde, écran non chargé) : écran d'erreur avec « Réessayer » et « Retour » ; un écran non chargé
 *   recharge d'abord la page une fois vers l'adresse demandée.
 * Les navigations lancées depuis l'écran de connexion n'affichent rien : la connexion suit elle-même son résultat.
 */
@Injectable({ providedIn: 'root' })
export class NavigationEtat {
  // #region Dépendances
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly collaborateurService = inject(CollaborateurService);
  private readonly deconnexion = inject(Deconnexion);
  // #endregion

  // #region État
  readonly chargement = signal(false);
  readonly barre = signal(false);
  readonly echec = signal<EchecNavigation | null>(null);

  /** Une page a été affichée dans cet onglet (sinon un échec laisserait une page blanche). */
  private affiche = false;
  /** Navigation en cours lancée depuis la connexion : rien n'est affiché (la connexion traite l'échec). */
  private silencieuse = false;
  private minuterie: ReturnType<typeof setTimeout> | undefined;
  // #endregion

  // #region Suivi de la navigation
  constructor() {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.debut(e.url);
      } else if (e instanceof NavigationEnd) {
        this.affiche = true;
        this.fin();
      } else if (e instanceof NavigationCancel) {
        this.fin();
        const refus =
          e.code === NavigationCancellationCode.GuardRejected || e.code === NavigationCancellationCode.NoDataFromResolver;
        if (refus && !this.affiche && !this.silencieuse) {
          this.echec.set({
            url: e.url,
            titre: 'Ouverture impossible',
            texte: "La page demandée n’a pas pu s’ouvrir.",
            rechargement: false,
          });
        }
      } else if (e instanceof NavigationError) {
        const ecranNonCharge = estErreurChargementEcran(e.error);
        // Écran toujours pas chargé après la reprise (avecReprise) : la page est rechargée une fois vers l'écran
        // demandé, ce qui corrige un fichier dépendant en échec (le chargement reste affiché jusque-là ; la connexion
        // garde la session, voir rechargementEnCours). Échec persistant : écran d'erreur.
        if (ecranNonCharge && rechargerApresEchecChargement(e.url)) {
          return;
        }
        this.fin();
        if (this.silencieuse) {
          return;
        }
        this.echec.set(
          ecranNonCharge
            ? {
                url: e.url,
                titre: "Impossible de charger l’écran",
                texte: 'Vérifiez votre connexion, puis réessayez.',
                rechargement: true,
              }
            : {
                url: e.url,
                titre: 'Ouverture impossible',
                texte: "Les informations nécessaires n’ont pas pu être chargées. Vérifiez votre connexion, puis réessayez.",
                rechargement: false,
              },
        );
      }
    });
  }
  // #endregion

  // #region Actions
  /** Libellé du bouton « Retour » de l'écran d'erreur. */
  libelleRetour(): string {
    if (this.affiche) {
      return 'Retour';
    }
    return LOGAIA_COLLAB_BUILD && this.collaborateurService.isCollab ? 'Liste des dossiers' : 'Retour à la connexion';
  }

  /** Réessayer : nouvelle navigation, ou rechargement de la page vers l'adresse demandée (écran non chargé). */
  reessayer(): void {
    const e = this.echec();
    if (!e) {
      return;
    }
    if (e.rechargement) {
      window.location.assign(e.url);
      return;
    }
    this.echec.set(null);
    this.router.navigateByUrl(e.url);
  }

  /** Retour : page déjà affichée (l'écran d'erreur se ferme), sinon liste des dossiers ou connexion. */
  retour(): void {
    this.echec.set(null);
    if (this.affiche) {
      return;
    }
    if (LOGAIA_COLLAB_BUILD && this.collaborateurService.isCollab) {
      this.router.navigateByUrl(URL_LISTE_DOSSIERS);
    } else {
      this.deconnexion.deconnecter();
    }
  }
  // #endregion

  // #region Utilitaires privés
  private debut(url: string): void {
    this.echec.set(null);
    clearTimeout(this.minuterie);
    const depuis = this.router.url;
    this.silencieuse = this.affiche && zoneNavigation(depuis) === 'signin';
    if (this.silencieuse || zoneNavigation(url) === 'signin') {
      return;
    }
    if (!this.affiche || zoneNavigation(url) !== zoneNavigation(depuis)) {
      this.chargement.set(true);
    } else {
      this.zone.runOutsideAngular(() => {
        this.minuterie = setTimeout(() => this.zone.run(() => this.barre.set(true)), DELAI_BARRE);
      });
    }
  }

  private fin(): void {
    clearTimeout(this.minuterie);
    this.chargement.set(false);
    this.barre.set(false);
  }
  // #endregion
}
